/**
 * Controlled vocabularies (F-02). Lists ported verbatim from the prototype's
 * `constants.json` and UI keep their original display strings as values. Lists
 * promoted to controlled vocabularies by DM-045 / R-15 use stable snake_case
 * codes plus English labels. Final values are confirmed with David (PRD Q15).
 */

export interface Vocab<T extends string> {
  readonly values: readonly [T, ...T[]]
  readonly labels: Readonly<Record<T, string>>
}

export type VocabValue<V> = V extends Vocab<infer T> ? T : never

export function vocab<const T extends readonly [string, ...string[]]>(
  values: T,
  labels: Record<T[number], string>,
): Vocab<T[number]> {
  return { values: values as unknown as readonly [T[number], ...T[number][]], labels }
}

/** Vocab whose values are their own labels (ported prototype lists). */
export function identityVocab<const T extends readonly [string, ...string[]]>(
  values: T,
): Vocab<T[number]> {
  const labels = Object.fromEntries(values.map((v) => [v, v])) as Record<T[number], string>
  return vocab(values, labels)
}

// --- Ported from constants.json -------------------------------------------

/** Assessment finding (DM-035, constants.json STATUSES). */
export const FINDINGS = identityVocab([
  'No material concern identified',
  'Further review required',
  'Substantiated norms concern',
  'Insufficient information',
])
export type Finding = VocabValue<typeof FINDINGS>

/** Assessment priority (DM-036). */
export const PRIORITIES = identityVocab([
  'Routine monitoring',
  'Watchlist / further review',
  'Active research / engagement',
  'Urgent review',
])
export type Priority = VocabValue<typeof PRIORITIES>

/** Ordered milestone stage ladder (DM-037). The order matters for reporting. */
export const STAGES = identityVocab([
  'S0 Baseline',
  'S1 Dialogue established',
  'S2 Commitment recorded',
  'S3 Implementation evidenced',
  'S4 Outcome evidenced',
])
export type Stage = VocabValue<typeof STAGES>

/** Security / holding mapping state (VAL-018). */
export const MAPPING_STATES = identityVocab([
  'verified',
  'needs review',
  'unresolved',
  'out of scope',
])
export type MappingState = VocabValue<typeof MAPPING_STATES>

/** Source class (VAL-022). */
export const SOURCE_CLASSES = identityVocab([
  'Official',
  'Company',
  'Media',
  'Civil society',
  'Internal record',
])
export type SourceClass = VocabValue<typeof SOURCE_CLASSES>

/** Source sharing flag (VAL-021). */
export const SHARING = vocab(['client', 'internal'], {
  client: 'Shareable with clients',
  internal: 'Internal to Fruuit',
})
export type Sharing = VocabValue<typeof SHARING>

/** Engagement (case) status (VAL-037). */
export const ENGAGEMENT_STATUSES = vocab(['active', 'stalled', 'closed'], {
  active: 'Active',
  stalled: 'Stalled',
  closed: 'Closed',
})
export type EngagementStatus = VocabValue<typeof ENGAGEMENT_STATUSES>

/** Internal screening run state (VAL-027). */
export const SEARCH_RUN_STATES = vocab(['complete', 'partial', 'failed'], {
  complete: 'Complete',
  partial: 'Partial',
  failed: 'Failed',
})
export type SearchRunState = VocabValue<typeof SEARCH_RUN_STATES>

/** Dialogue (correspondence) event type, from the shipped UI list (GAP-DD-005). */
export const DIALOGUE_EVENT_TYPES = vocab(
  ['written_response', 'company_meeting_record', 'evidence_received', 'follow_up_request'],
  {
    written_response: 'Written response',
    company_meeting_record: 'Company meeting record',
    evidence_received: 'Evidence received',
    follow_up_request: 'Follow-up request',
  },
)
export type DialogueEventType = VocabValue<typeof DIALOGUE_EVENT_TYPES>

/** Meeting type (CAL-022). */
export const MEETING_TYPES = identityVocab([
  'Company dialogue',
  'Investor briefing',
  'Evidence review',
  'Follow-up',
])
export type MeetingType = VocabValue<typeof MEETING_TYPES>

/** Participation request type (PART-005). */
export const PARTICIPATION_REQUEST_TYPES = identityVocab([
  'Discuss participation',
  'Join a future company meeting',
  'Receive the next evidence update',
])
export type ParticipationRequestType = VocabValue<typeof PARTICIPATION_REQUEST_TYPES>

/** Client action (PORT-013). Client actions never change findings (PORT-015). */
export const CLIENT_ACTIONS = identityVocab([
  'Continue monitoring',
  'Request deeper research',
  'Discuss escalation',
  'Consider a portfolio restriction',
])
export type ClientActionType = VocabValue<typeof CLIENT_ACTIONS>

export const CLIENT_ACTION_STATUSES = identityVocab(['Recorded for discussion', 'Discussed'])
export type ClientActionStatus = VocabValue<typeof CLIENT_ACTION_STATUSES>

// --- Promoted controlled lists (DM-045, R-15) ------------------------------

export const SEVERITIES = vocab(
  ['not_identified', 'low', 'moderate', 'serious', 'severe', 'undetermined'],
  {
    not_identified: 'No material harm identified',
    low: 'Low',
    moderate: 'Moderate',
    serious: 'Serious',
    severe: 'Severe',
    undetermined: 'Cannot be determined',
  },
)
export type Severity = VocabValue<typeof SEVERITIES>

export const CONFIDENCE_LEVELS = vocab(['high', 'moderate', 'low', 'insufficient'], {
  high: 'High',
  moderate: 'Moderate',
  low: 'Low',
  insufficient: 'Insufficient evidence',
})
export type Confidence = VocabValue<typeof CONFIDENCE_LEVELS>

export const INVOLVEMENT_TYPES = vocab(
  [
    'direct_operations',
    'controlled_entity',
    'joint_venture',
    'business_relationship',
    'financing',
    'products_services',
    'unresolved',
  ],
  {
    direct_operations: 'Direct operations',
    controlled_entity: 'Subsidiary or controlled entity',
    joint_venture: 'Joint venture or minority stake',
    business_relationship: 'Supply chain or business relationship',
    financing: 'Financing or investment',
    products_services: 'Products or services',
    unresolved: 'Unresolved',
  },
)
export type Involvement = VocabValue<typeof INVOLVEMENT_TYPES>

/** Engagement theme and objective category. */
export const THEMES = vocab(
  [
    'climate',
    'nature',
    'human_rights',
    'labour_rights',
    'governance',
    'anti_corruption',
    'health_safety',
    'communities',
  ],
  {
    climate: 'Climate',
    nature: 'Nature and biodiversity',
    human_rights: 'Human rights',
    labour_rights: 'Labour rights',
    governance: 'Governance',
    anti_corruption: 'Anti-corruption',
    health_safety: 'Health and safety',
    communities: 'Communities and land rights',
  },
)
export type Theme = VocabValue<typeof THEMES>

/** Category of a world event, incident or feed signal. */
export const EVENT_TYPES = vocab(
  [
    'environmental_spill',
    'emissions_breach',
    'labour_dispute',
    'worker_safety',
    'supply_chain_abuse',
    'community_conflict',
    'sanctions_exposure',
    'corruption_allegation',
    'governance_scandal',
    'data_privacy',
    'product_safety',
    'regulatory_action',
    'litigation',
    'positive_remediation',
  ],
  {
    environmental_spill: 'Environmental spill or release',
    emissions_breach: 'Emissions or permit breach',
    labour_dispute: 'Labour dispute',
    worker_safety: 'Worker health and safety incident',
    supply_chain_abuse: 'Supply-chain labour abuse',
    community_conflict: 'Community or land conflict',
    sanctions_exposure: 'Sanctions exposure',
    corruption_allegation: 'Corruption or bribery allegation',
    governance_scandal: 'Governance failure',
    data_privacy: 'Data privacy breach',
    product_safety: 'Product safety issue',
    regulatory_action: 'Regulatory action',
    litigation: 'Litigation',
    positive_remediation: 'Remediation or positive development',
  },
)
export type EventType = VocabValue<typeof EVENT_TYPES>

export const SECTORS = vocab(
  [
    'energy',
    'materials',
    'mining',
    'utilities',
    'industrials',
    'transport',
    'infrastructure',
    'consumer_goods',
    'food_agriculture',
    'health_care',
    'financials',
    'real_estate',
    'technology',
    'telecommunications',
  ],
  {
    energy: 'Energy',
    materials: 'Materials',
    mining: 'Mining and metals',
    utilities: 'Utilities',
    industrials: 'Industrials',
    transport: 'Transport',
    infrastructure: 'Infrastructure',
    consumer_goods: 'Consumer goods',
    food_agriculture: 'Food and agriculture',
    health_care: 'Health care',
    financials: 'Financials',
    real_estate: 'Real estate',
    technology: 'Technology',
    telecommunications: 'Telecommunications',
  },
)
export type Sector = VocabValue<typeof SECTORS>

/** Structured source rights (R-15, GAP-DD-004). */
export const RIGHTS_BASIS = vocab(
  ['licensed_feed', 'company_disclosure', 'public_record', 'original_analysis', 'client_provided'],
  {
    licensed_feed: 'Licensed data feed',
    company_disclosure: 'Company disclosure',
    public_record: 'Public record',
    original_analysis: 'Original Fruuit analysis',
    client_provided: 'Provided by the client',
  },
)
export type RightsBasis = VocabValue<typeof RIGHTS_BASIS>

export const REDISTRIBUTION = vocab(['allowed', 'internal_only'], {
  allowed: 'Redistribution allowed',
  internal_only: 'Internal use only',
})
export type Redistribution = VocabValue<typeof REDISTRIBUTION>

/** Security identifier types (R-15, GAP-DD-003). ISIN keeps VAL-016/017. */
export const SECURITY_IDENTIFIER_TYPES = vocab(
  ['ISIN', 'CUSIP', 'SEDOL', 'FIGI', 'PROVIDER_ID', 'OTHER'],
  {
    ISIN: 'ISIN',
    CUSIP: 'CUSIP',
    SEDOL: 'SEDOL',
    FIGI: 'FIGI',
    PROVIDER_ID: 'Provider security id',
    OTHER: 'Other',
  },
)
export type SecurityIdentifierType = VocabValue<typeof SECURITY_IDENTIFIER_TYPES>

/** Issuer identifier types (ARCHITECTURE §8 + R-15). */
export const ISSUER_IDENTIFIER_TYPES = vocab(['LEI', 'ISIN', 'PERMID', 'PROVIDER_ID', 'OTHER'], {
  LEI: 'LEI',
  ISIN: 'ISIN',
  PERMID: 'PermID',
  PROVIDER_ID: 'Provider issuer id',
  OTHER: 'Other',
})
export type IssuerIdentifierType = VocabValue<typeof ISSUER_IDENTIFIER_TYPES>

/** Partial-date precision for evidence dates (R-16). Unknown dates are null. */
export const DATE_PRECISIONS = vocab(['day', 'month', 'year', 'unknown'], {
  day: 'Exact day',
  month: 'Month only',
  year: 'Year only',
  unknown: 'Unverified',
})
export type DatePrecision = VocabValue<typeof DATE_PRECISIONS>

// --- Workflow states -------------------------------------------------------

/** Review/release state of reviewed kinds (DM-038 + GAP-DD-009). */
export const REVIEW_STATES = vocab(
  ['draft', 'in_review', 'approved', 'published', 'returned', 'retracted'],
  {
    draft: 'Draft',
    in_review: 'In review',
    approved: 'Approved',
    published: 'Published',
    returned: 'Returned for changes',
    retracted: 'Retracted',
  },
)
export type ReviewState = VocabValue<typeof REVIEW_STATES>

export const REVIEW_ACTIONS = vocab(['submit', 'approve', 'publish', 'return', 'retract'], {
  submit: 'Submit for review',
  approve: 'Approve',
  publish: 'Publish',
  return: 'Return for changes',
  retract: 'Retract',
})
export type ReviewAction = VocabValue<typeof REVIEW_ACTIONS>

export const MEETING_STATES = vocab(['draft', 'published', 'cancelled', 'superseded'], {
  draft: 'Draft',
  published: 'Published',
  cancelled: 'Cancelled',
  superseded: 'Rescheduled',
})
export type MeetingState = VocabValue<typeof MEETING_STATES>

/** Participation state machine (P-PART-001, R-11). */
export const PARTICIPATION_STATES = vocab(
  ['requested', 'acknowledged', 'company_consent_pending', 'confirmed', 'declined', 'withdrawn'],
  {
    requested: 'Requested',
    acknowledged: 'Acknowledged',
    company_consent_pending: 'Awaiting company consent',
    confirmed: 'Confirmed',
    declined: 'Declined',
    withdrawn: 'Withdrawn',
  },
)
export type ParticipationState = VocabValue<typeof PARTICIPATION_STATES>

export const QUESTION_STATUSES = vocab(['open', 'answered', 'closed'], {
  open: 'Awaiting reply',
  answered: 'Answered',
  closed: 'Closed',
})
export type QuestionStatus = VocabValue<typeof QUESTION_STATUSES>

export const SIGNAL_TRIAGE_STATES = vocab(
  ['new', 'mapping_review', 'duplicate', 'promoted', 'dismissed'],
  {
    new: 'New',
    mapping_review: 'Mapping review',
    duplicate: 'Duplicate or linked',
    promoted: 'Promoted to assessment work',
    dismissed: 'Dismissed',
  },
)
export type SignalTriageState = VocabValue<typeof SIGNAL_TRIAGE_STATES>

export const MAPPING_EXCEPTION_STATUSES = vocab(['open', 'resolved', 'dismissed'], {
  open: 'Open',
  resolved: 'Resolved',
  dismissed: 'Dismissed',
})
export type MappingExceptionStatus = VocabValue<typeof MAPPING_EXCEPTION_STATUSES>

// --- Feed (M10) ------------------------------------------------------------

export const FEED_PROVIDERS = vocab(['fruuit-screening', 'news-monitor', 'company-disclosures'], {
  'fruuit-screening': 'Fruuit Screening Engine (AI-assisted)',
  'news-monitor': 'News monitor',
  'company-disclosures': 'Company disclosures',
})
export type FeedProviderId = VocabValue<typeof FEED_PROVIDERS>

export const FEED_DATASETS = vocab(
  ['fruuit-screening.signals', 'news-monitor.articles', 'company-disclosures.filings'],
  {
    'fruuit-screening.signals': 'Screening signals',
    'news-monitor.articles': 'News articles',
    'company-disclosures.filings': 'Company filings',
  },
)
export type FeedDataset = VocabValue<typeof FEED_DATASETS>

export const LICENCE_STATUSES = vocab(['simulated', 'pending', 'licensed', 'expired'], {
  simulated: 'Demo data',
  pending: 'Licence pending',
  licensed: 'Licensed',
  expired: 'Licence expired',
})
export type LicenceStatus = VocabValue<typeof LICENCE_STATUSES>

export const FEED_RUN_STATUSES = vocab(['running', 'ok', 'partial', 'empty', 'failed', 'late'], {
  running: 'Running',
  ok: 'Succeeded',
  partial: 'Partial',
  empty: 'No new records',
  failed: 'Failed',
  late: 'Late',
})
export type FeedRunStatus = VocabValue<typeof FEED_RUN_STATUSES>

export const FEED_RUN_TRIGGERS = vocab(['cron', 'manual', 'retry'], {
  cron: 'Scheduled',
  manual: 'Manual re-run',
  retry: 'Automatic retry',
})
export type FeedRunTrigger = VocabValue<typeof FEED_RUN_TRIGGERS>

/** Fixed feed error taxonomy (GAP-TP-012). Provider body text is never stored. */
export const FEED_ERROR_CODES = vocab(
  [
    'timeout',
    'http_error',
    'auth_failed',
    'rate_limited',
    'redirect_refused',
    'size_exceeded',
    'invalid_payload',
    'mapping_failed',
    'provider_unavailable',
    'unknown',
  ],
  {
    timeout: 'Timed out',
    http_error: 'HTTP error',
    auth_failed: 'Authentication failed',
    rate_limited: 'Rate limited',
    redirect_refused: 'Redirect refused',
    size_exceeded: 'Response too large',
    invalid_payload: 'Invalid payload',
    mapping_failed: 'Identifier mapping failed',
    provider_unavailable: 'Provider unavailable',
    unknown: 'Unknown error',
  },
)
export type FeedErrorCode = VocabValue<typeof FEED_ERROR_CODES>

export const ALERT_KINDS = vocab(
  ['feed_failed', 'feed_partial', 'feed_late', 'audit_verify_failed', 'job_failed'],
  {
    feed_failed: 'Feed run failed',
    feed_partial: 'Feed run partial',
    feed_late: 'Feed delivery late',
    audit_verify_failed: 'Audit chain verification failed',
    job_failed: 'Scheduled job failed',
  },
)
export type AlertKind = VocabValue<typeof ALERT_KINDS>

export const ALERT_SEVERITIES = vocab(['info', 'warning', 'critical'], {
  info: 'Information',
  warning: 'Warning',
  critical: 'Critical',
})
export type AlertSeverity = VocabValue<typeof ALERT_SEVERITIES>

// --- Investor conveniences -------------------------------------------------

export const SAVED_VIEW_SCOPES = vocab(['portfolio', 'report', 'engagements', 'calendar'], {
  portfolio: 'Portfolio',
  report: 'Report',
  engagements: 'Engagements',
  calendar: 'Calendar',
})
export type SavedViewScope = VocabValue<typeof SAVED_VIEW_SCOPES>

/** Derived, investor-safe coverage labels (R-04, API-009). */
export const COVERAGE_LABELS = identityVocab([
  'Within recorded scope',
  'Review overdue',
  'Coverage gap: source check partial',
  'Coverage gap: source check failed',
  'Source review not completed',
])
export type CoverageLabel = VocabValue<typeof COVERAGE_LABELS>

export const EXPORT_FORMATS = vocab(['csv', 'html'], { csv: 'CSV', html: 'Print HTML' })
export type ExportFormat = VocabValue<typeof EXPORT_FORMATS>
